/**
 * Razvan Garage – cautare stricta.
 *
 * Cautarea magazinului (cu intelegere "semantica") adauga la rezultate si produse care contin doar
 * unul dintre cuvinte: "covorase tiguan" aducea toate covorasele din magazin, iar filtrele din stanga
 * se numarau pe toate. Aici fiecare cuvant tastat se trimite in ghilimele ("covorase" "tiguan"),
 * asa ca raman numai produsele care contin TOATE cuvintele, iar filtrele se aplica doar pe ele.
 *
 * - Textele cu coduri de piese (un cuvant cu cel putin 3 cifre, in afara de viscozitati ca 5W30)
 *   raman neschimbate: de ele se ocupa
 *   cautarea obisnuita impreuna cu rg-cautare-cod.js.
 * - Cuvintele generale ("interior", "auto", "pentru", "set"...) nu apar de obicei in titluri, asa ca
 *   nu sunt obligatorii: "covorase interior" se trimite ca "covorase" interior. La fel motorizarea
 *   ("1.4", "tdci", "benzina", "90cp") si "jante" langa "capace" (titlurile spun "capace roti").
 * - Cuvintele care numesc felul produsului, dar lipsesc din multe titluri ("tavita": la Umbrella
 *   titlul e "Covor protectie portbagaj"), se cauta dupa tipul produsului:
 *   "tavita portbagaj logan" -> product_type:"Covoras portbagaj" "logan";
 *   "camera retur ducato" = "camera marsarier ducato" -> product_type:"Camera retur" "ducato".
 *   Textul tastat merge alaturi, in parametrul rg_q, ca sa apara la fel in caseta si in mesaje.
 * - "baterie 74ah" = "acumulator 74 ah": titlurile spun cand "Baterie", cand "Acumulator", cand "74Ah", cand
 *   "74 Ah", asa ca amperajul se cauta cu filtrul Amperaj (il au doar bateriile de pornire, nu si pilele), iar
 *   "baterie", "acumulator", "baterie auto" cu filtrul Tip produs. Cautarea simpla dupa "baterie" (fara
 *   ghilimele) le gaseste pe toate, si dupa tipul produsului, nu doar dupa titlu.
 * - "anvelope iarna", "cauciucuri 185/65 r15": se cauta dupa tipul Anvelopa, iar marimea scrisa oricum ("185 65 15")
 *   ajunge in forma din titluri ("185/65" "R15"). "spray anvelope" si "cauciucuri bara" raman la cautarea pe cuvinte.
 * - Daca cautarea stricta nu gaseste nimic (de ex. o greseala de tastare), pagina trece singura la
 *   cautarea obisnuita (rg_lax=1) si spune asta clientului.
 * - In caseta de cautare si in titlul paginii textul apare fara ghilimele.
 * - Viscozitatea scrisa cu liniuta sau spatiu ("5w-30", "5w 30") se cauta ca in titluri ("5w30").
 * - Anul (04.11): "covorase tucson 2023" se cauta ca "covorase" "tucson" cu filtrul An fabricatie = 2023 (anii vin
 *   din intervalul din titlu: "2021-" acopera 2023). Daca anul nu da nimic, se cauta numarul ca text
 *   ("filtru john deere 2030" = model, nu an), apoi fara el, cu mesaj. "Peugeot 2008" e model, nu an.
 * - Modelele numerice dupa marca (04.11), doar la accesorii cu modelul in titlu (covorase, tavite, huse, deflectoare):
 *   "covorase peugeot 2008", "huse fiat 500" se cauta exact (numarul nu mai e luat drept cod de piesa).
 *   La piese ("filtru ulei peugeot 206") ramane cautarea obisnuita.
 */
(() => {
  if (window.rgCautareStricta) return;

  const faraDiacritice = (text) => (text || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

  // Cuvinte care nu sunt obligatorii (doar ajuta la ordinea rezultatelor).
  const GENERALE = new Set([
    'interior', 'auto', 'masina', 'masini', 'autoturism', 'pentru', 'pt', 'de', 'cu', 'si', 'la', 'din', 'set',
    'original', 'originale', 'ieftin', 'ieftine', 'nou', 'noi', 'noua',
  ]);
  // Motorizarea ("1.4", "tdci", "dci", "benzina", "90cp") e in compatibilitati, rar in titluri: nu e obligatorie.
  const MOTORIZARE = new Set([
    'tdci', 'tdi', 'dci', 'hdi', 'bluehdi', 'cdi', 'crdi', 'cdti', 'dti', 'sdi', 'tsi', 'tfsi', 'fsi', 'gdi', 'tce',
    'jtd', 'jtdm', 'multijet', 'mjet', 'vtec', 'puretech', 'ecoboost', 'd4d', 'benzina', 'diesel', 'motorina', 'gpl',
  ]);
  const esteMotorizare = (cuvant) => {
    const c = faraDiacritice(cuvant);
    return MOTORIZARE.has(c) || /^\d[.,]\d{1,2}$/.test(c) || /^\d{2,3}(cp|kw|hp)$/.test(c);
  };
  // Unitatile de masura ("100ah", "12v", "30a", "5l") raman obligatorii – titlurile le au de obicei asa
  // ("baterie 100ah": 2 rezultate exacte, nu 132) –, dar nu sunt coduri de piesa.
  const esteUnitate = (cuvant) => /^\d+([.,]\d+)?(l|ml|kg|g|w|v|a|ah|mm|cm|bar|t)$/.test(faraDiacritice(cuvant));
  const esteGeneral = (cuvant) => GENERALE.has(faraDiacritice(cuvant)) || esteMotorizare(cuvant);

  // Bateriile de pornire: tipul din filtrul Tip produs, cuvintele care le numesc si cele care arata alt produs.
  const AMPERAJ = 'filter.p.m.custom.amperaj';
  const TIP_PRODUS = 'filter.p.m.custom.tip_produs_unic';
  const AN_FAB = 'filter.p.m.custom.an_fabricatie';
  const ESTE_AN = /^(19[5-9]\d|20[0-3]\d)$/;
  // Dupa aceste marci numarul e model, nu an: Peugeot 2008 / 3008, John Deere 2030, BMW 2002.
  const MODEL_NUMERIC = new Set(['peugeot', 'deere', 'bmw']);
  // Masinile cu modele numerice: numarul de dupa marca e model, nu cod de piesa, deci se cauta exact
  // ("covorase peugeot 2008": 18 rezultate in loc de 665; "fiat 500": 4 in loc de 333). Negasit -> cautarea obisnuita.
  const MARCI_MODEL_NUMERIC = new Set(['peugeot', 'fiat', 'abarth', 'volvo', 'alfa', 'romeo', 'mazda', 'rover', 'saab',
    'dacia', 'lada']);
  // Doar la accesoriile care au modelul in titlu. La piese compatibilitatea e in descriere: "filtru ulei peugeot 206"
  // exact ar da 1 rezultat, asa ca ramane la cautarea obisnuita.
  const ACCESORII_CU_MODEL = new Set(['covor', 'covoras', 'covorase', 'covoare', 'presuri', 'tavita', 'tavite', 'husa', 'huse',
    'deflector', 'deflectoare', 'paravant', 'paravanturi', 'aparatori', 'aparatoare', 'cotiera', 'cotiere', 'portbagaj']);
  const BATERIE = {
    tip: 'gid://shopify/Metaobject/598435561815',
    cuvinte: ['baterie', 'baterii', 'bateria', 'bateriei', 'bateriile', 'acumulator', 'acumulatori', 'acumulatoare',
      'acumulatorul', 'acumulatorului'],
    masina: ['auto', 'masina', 'masini', 'autoturism', 'pentru', 'pt', 'de'],
    marci: ['varta', 'rombat', 'bosch', 'mtr', 'motrio', 'optima', 'granit'],
    fara: ['borna', 'borne', 'papuc', 'papuci', 'clema', 'cleme', 'cablu', 'cabluri', 'tester', 'testere', 'redresor',
      'redresoare', 'incarcator', 'incarcatoare', 'robot', 'capac', 'capace', 'suport', 'suporti', 'cutie', 'carcasa',
      'externa', 'externe', 'telecomanda', 'telecomenzi', 'ceas', 'cheie', 'chei', 'lanterna', 'laptop', 'telefon',
      'hidraulic', 'hidraulica', 'socuri', 'filtre', 'filtru', 'prefiltru', 'scule', 'bormasina', 'surubelnita',
      'milwaukee', 'makita', 'dewalt', 'umplere', 'aa', 'aaa', 'alcalina', 'alcaline', 'litiu', 'reincarcabila',
      'reincarcabile', 'camera'],
  };

  /**
   * Cautarea unei baterii de pornire, cand textul o cere.
   * @param {string} text - Textul tastat.
   * @returns {{q: string, filtru: string[]}|null} Textul de cautare si filtrul sau null cand nu se aplica.
   */
  function baterie(text) {
    const cuvinte = faraDiacritice(text).trim().replace(/(\d)\s*(ah|amperi|amper)\b/g, '$1ah').split(/\s+/);
    const rest = cuvinte.filter((c) => !BATERIE.cuvinte.includes(c));
    if (rest.length === cuvinte.length) return null;
    // Alte produse, coduri ("baterie 574012068") si alte tensiuni ("acumulator 18v", "baterie 1.5v").
    if (rest.some((c) => BATERIE.fara.includes(c) || /\d{4}/.test(c) || (/^[\d.,]+v$/.test(c) && !/^(6|12|24)v$/.test(c)))) return null;

    const ah = rest.map((c) => (c.match(/^(\d{1,3})ah$/) || c.match(/^(\d{2,3})$/) || [])[1]).find(Boolean);
    const marca = rest.filter((c) => BATERIE.marci.includes(c)).map((c) => `"${c}"`).join(' ');
    if (ah) return { q: marca || 'baterie', filtru: [AMPERAJ, `${ah} Ah`] };

    const langa = rest.filter((c) => !BATERIE.marci.includes(c) && !/^(6|12|24)v$/.test(c));
    if (rest.length && !(langa.length && langa.every((c) => BATERIE.masina.includes(c)))) return null;
    return { q: marca || 'baterie', filtru: [TIP_PRODUS, BATERIE.tip] };
  }

  // Cuvinte care nu sunt obligatorii doar langa altele: "capace jante 15" -> titlurile spun "capace roti";
  // "kit intaritor" -> titlul spune doar "Intaritor".
  const OPTIONALE_LANGA = [
    { daca: ['capace', 'capac'], optionale: ['jante', 'janta', 'jantei', 'roti', 'roata'] },
    { daca: null, optionale: ['kit'] },
    // "casetofon / radio cu navigatie": titlurile spun doar "Navigatie".
    { daca: ['navigatie', 'navigatii'], optionale: ['casetofon', 'radio', 'sistem', 'player'] },
    // "baterie varta": titlurile spun cand "Baterie Varta", cand "Acumulator Varta".
    { daca: ['varta', 'rombat'], optionale: BATERIE.cuvinte },
  ];

  // Forma din titluri doar langa alt cuvant: "husa cheie" -> "carcasa cheie" (40 de titluri / 1);
  // "covorase tractor" -> "covoras tractor" (toate cele 28 de titluri).
  const FORMA_LANGA = [
    { daca: ['cheie'], schimba: { husa: 'carcasa', huse: 'carcasa' } },
    { daca: ['tractor', 'tractoare'], schimba: { covorase: 'covoras', covor: 'covoras', covoare: 'covoras' } },
    // "adaptor camera retur" -> "camera marsarier" (titlurile spun "marsarier"); plus scrierile din rapoartele de cautare.
    {
      daca: ['camera', 'camere', 'camerei'],
      schimba: { retur: 'marsarier', retir: 'marsarier', marsalier: 'marsarier', mansalier: 'marsarier', masalier: 'marsarier' },
    },
    // Titlurile spun "Baterie tractor / moto", dar "Acumulator Bosch / MTR / Motrio".
    {
      daca: ['tractor', 'tractoare', 'moto'],
      schimba: { acumulator: 'baterie', acumulatori: 'baterie', acumulatoare: 'baterie', baterii: 'baterie' },
    },
    { daca: ['bosch', 'mtr', 'motrio'], schimba: { baterie: 'acumulator', baterii: 'acumulator', acumulatori: 'acumulator' } },
  ];

  // Forma din titluri (numarata pe produsele active, 23.10.2026): "faruri" -> "far" (493 de titluri cu „far”,
  // 86 cu „faruri”), "placuta" -> "placute" (3.020 / 66). Doar unde o forma bate clar; plus scrieri des intalnite.
  const FORMA_TITLU = {
    faruri: 'far', chei: 'cheie', manometre: 'manometru', mufe: 'mufa', arcuri: 'arc', oglinzi: 'oglinda',
    stopuri: 'stop', discuri: 'disc', filtre: 'filtru', rulmenti: 'rulment', amortizoare: 'amortizor',
    senzori: 'senzor', curele: 'curea', lampi: 'lampa', pompe: 'pompa', conducte: 'conducta', furtunuri: 'furtun',
    termostate: 'termostat', radiatoare: 'radiator', bobine: 'bobina', injectoare: 'injector',
    semnalizari: 'semnalizare', comutatoare: 'comutator', cabluri: 'cablu', antene: 'antena', rame: 'rama',
    geamuri: 'geam', usi: 'usa', aripi: 'aripa', bare: 'bara', suporti: 'suport', suporturi: 'suport',
    electromotoare: 'electromotor', alternatoare: 'alternator', placuta: 'placute',
    carcase: 'carcasa', cotiere: 'cotiera', reparare: 'reparatie', obd2: 'obd', obdii: 'obd',
    umidimetru: 'umidometru', briliante: 'brilliante', heppu: 'hepu', vaccum: 'vacuum', vacum: 'vacuum',
  };
  const optionaleIn = (cuvinte) => {
    const n = cuvinte.map(faraDiacritice);
    return new Set(OPTIONALE_LANGA
      .filter(({ daca, optionale }) => (daca ? n.some((c) => daca.includes(c)) : n.some((c) => !optionale.includes(c))))
      .flatMap(({ optionale }) => optionale));
  };

  const ANVELOPE = ['anvelopa', 'anvelope', 'anvelopei', 'anvelopele', 'anvelopelor'];
  // Marimea unei anvelope, scrisa oricum: "185/65 r15", "185/65r15", "185 65 15", "500/50-17".
  const MARIME_ANVELOPA = /\b(\d{3})[\s\/-]+(\d{2})([\s\/-]*)(r?)\s?(\d{2}(?:[.,]5)?)(c?)\b/i;

  // Cuvinte cautate dupa tipul produsului; "insotitoare" se scot, fiind deja in tip.
  const TIPURI = [
    {
      cuvinte: ['tavita', 'tavite', 'tavitele', 'tavitelor'],
      insotitoare: ['portbagaj', 'protectie', 'covor', 'covoras', 'covorase'],
      tip: 'Covoras portbagaj',
    },
    {
      // "covor portbagaj logan": la Umbrella titlul e "Covor protectie portbagaj", la Frogum "Tavita portbagaj".
      cuvinte: ['covor', 'covoras', 'covorase'],
      cere: ['portbagaj'],
      insotitoare: ['portbagaj', 'protectie'],
      tip: 'Covoras portbagaj',
    },
    {
      // "camera retur / marsarier / mers inapoi": titlurile spun cand una, cand alta, dar toate camerele de
      // marsarier au tipul "Camera retur". Accesoriile ("adaptor camera marsarier") raman la cautarea pe cuvinte.
      cuvinte: ['camera', 'camere', 'camerei'],
      cere: ['marsarier', 'inapoi', 'spate', 'spatele'],
      fara: ['adaptor', 'adaptoare', 'releu', 'mufa', 'mufe', 'cablu', 'cabluri', 'cablaj', 'splitter', 'plitter',
        'monitor', 'monitoare', 'filtru', 'stabilizator', 'stabilizare', 'modul', 'interfata', 'navigatie', 'navigatii'],
      insotitoare: ['marsarier', 'mers', 'inapoi', 'spate', 'spatele', 'video'],
      tip: 'Camera retur',
    },
    {
      // "capace roti", "capace jante 15": "capace" aduce si capacele de culbutori sau de oglinda, iar filtrele din
      // stanga se numarau pe toate. Capacele de butuc si de prezon raman la cautarea pe cuvinte.
      cuvinte: ['capace', 'capacele'],
      cere: ['roti', 'roata', 'jante', 'janta', 'jantei', '13', '14', '15', '16', '17', '18'],
      fara: ['butuc', 'butuci', 'prezon', 'prezoane', 'piulita', 'piulite', 'central', 'centrale', 'centru'],
      insotitoare: ['roti', 'roata', 'jante', 'janta', 'jantei', 'inch', 'inci', 'toli'],
      tip: 'Capac roata',
    },
    {
      // Singularul doar cu "roata/roti": "capac janta" e de obicei capacul mic de centru, care are alt tip.
      cuvinte: ['capac'],
      cere: ['roata', 'roti'],
      fara: ['butuc', 'butuci', 'prezon', 'prezoane', 'piulita', 'piulite', 'central', 'centrale', 'centru'],
      insotitoare: ['roata', 'roti', 'inch', 'inci', 'toli'],
      tip: 'Capac roata',
    },
    {
      // "anvelope" prinde si spray-urile si husele, iar "anvelope iarna" nicio anvelopa: titlurile spun "Anvelopa".
      // Accesoriile ("spray anvelope", "camera anvelopa") raman la cautarea pe cuvinte.
      cuvinte: ANVELOPE,
      fara: ['spray', 'husa', 'huse', 'valva', 'valve', 'ventil', 'ventile', 'surubelnita', 'aparat', 'furtun', 'solutie',
        'gel', 'kit', 'trusa', 'reparatie', 'reparatii', 'reparat', 'antipana', 'etansant', 'etansare', 'umflat', 'umflare',
        'umplere', 'pistol', 'manometru', 'compresor', 'pompa', 'senzor', 'presiune', 'suport', 'rastel', 'roata', 'roti',
        'camera', 'camere', 'talon', 'curatat', 'curatare', 'luciu', 'cric', 'demontat', 'dezechipat', 'montat', 'profil'],
      insotitoare: ['turisme', 'tractor', 'tractoare', 'remorca', 'remorci', 'agricole', 'utilitare', 'utilitara'],
      tip: 'Anvelopa',
    },
  ];

  // Bucati stricte: "cuvant" sau camp:"valoare".
  const BUCATA_STRICTA = /(\w+:)?"[^"]+"/g;

  const esteStrict = (text) => {
    const t = (text || '').trim();
    if (!/"[^"]+"/.test(t)) return false;
    const rest = t.replace(BUCATA_STRICTA, ' ').trim();
    const optionale = optionaleIn(t.replace(/\w+:"[^"]+"/g, ' ').replace(/"/g, ' ').trim().split(/\s+/));
    return !rest || rest.split(/\s+/).every((c) => esteGeneral(c) || optionale.has(faraDiacritice(c)));
  };
  const faraGhilimele = (text) => (text || '').replace(/\w+:"[^"]+"/g, ' ').replace(/"/g, ' ').replace(/\s+/g, ' ').trim();
  const esteCautare = (url) => /\/search\/?$/.test(url.pathname);

  /**
   * Textul cautat in forma stricta: fiecare cuvant in ghilimele.
   * @param {string} text - Textul tastat.
   * @returns {string} Textul strict sau textul neschimbat cand nu se aplica.
   */
  function strict(text) {
    const curat = (text || '').trim();
    if (!curat) return curat;
    // Sintaxa scrisa de client (ghilimele, campuri, operatori) ramane cum e.
    if (/["():*]/.test(curat) || /(^|\s)(OR|AND|NOT)(\s|$)/.test(curat) || /(^|\s)-\S/.test(curat)) return curat;

    let cuvinte = curat.split(/\s+/);

    // Marimea anvelopei in forma din titluri ("185/65" "R15"). Cu "R" se intelege ca e anvelopa si fara cuvantul
    // "anvelope". Titlurile Barum nu scriu "C" ("215/65 R16 109/107"), iar cele agricole nu au "R" ("500/50-17").
    const langaAnvelopa = (c) => [...ANVELOPE, 'cauciucuri'].includes(faraDiacritice(c));
    const marime = curat.match(MARIME_ANVELOPA);
    let marimi = [];
    if (marime && (marime[4] || cuvinte.some(langaAnvelopa))) {
      marimi = [`${marime[1]}/${marime[2]}`];
      if (!marime[3].includes('-') && !marime[6]) marimi.push(`R${marime[5]}`);
      cuvinte = curat.replace(MARIME_ANVELOPA, ' ').trim().split(/\s+/).filter(Boolean);
      if (!cuvinte.some(langaAnvelopa)) cuvinte.push('anvelope');
      cuvinte.push(...marimi);
    }
    // "cauciucuri" e anvelopa doar singur sau langa sezon si marime: "cauciucuri bara" sunt piese de cauciuc.
    if (cuvinte.every((c) => ['cauciucuri', 'iarna', 'vara'].includes(faraDiacritice(c)) || marimi.includes(c))) {
      cuvinte = cuvinte.map((c) => (faraDiacritice(c) === 'cauciucuri' ? 'anvelope' : c));
    }

    // Viscozitatile ("5W30", "10W-40") nu sunt coduri.
    // Nici motorizarea ("140cp", "1.9") nu e cod.
    // Modelul numeric de dupa marca ("peugeot 2008", "peugeot 307 308", "alfa romeo 147") nu e cod.
    const modele = new Set();
    const accesoriu = cuvinte.some((c) => ACCESORII_CU_MODEL.has(faraDiacritice(c)));
    if (accesoriu) cuvinte.forEach((c, i) => {
      const prev = faraDiacritice(cuvinte[i - 1] || '');
      if (/^\d{3,4}$/.test(c) && (MARCI_MODEL_NUMERIC.has(prev) || modele.has(i - 1))) modele.add(i);
    });
    const esteCod = (cuvant, i) => (cuvant.match(/\d/g) || []).length >= 3 && !/^\d{1,2}w-?\d{2}$/i.test(cuvant)
      && !esteMotorizare(cuvant) && !esteUnitate(cuvant) && !marimi.includes(cuvant) && !modele.has(i);
    if (cuvinte.some(esteCod)) return curat;

    // Marimea rotii langa "capace": titlurile scriu "15", nu "R15".
    if (cuvinte.some((c) => ['capace', 'capac'].includes(faraDiacritice(c)))) {
      cuvinte = cuvinte.map((c) => (/^r(1[2-9]|2[0-2])$/i.test(c) ? c.slice(1) : c));
    }

    // Viscozitatea ca in titluri (04.11): "5w-30" si "5w 30" -> "5w30" ("ulei motor 5w-30": 69 de produse, "5w30": 316).
    cuvinte = cuvinte.reduce((acc, c) => {
      const prev = acc[acc.length - 1];
      if (prev && /^\d{1,2}w$/i.test(prev) && /^\d{2}$/.test(c)) acc[acc.length - 1] = prev + c;
      else acc.push(c);
      return acc;
    }, []).map((c) => c.replace(/^(\d{1,2})w-(\d{2})$/i, '$1w$2'));

    // Forma din titluri ("faruri" -> "far") si scrierile des intalnite ("umidimetru" -> "umidometru").
    cuvinte = cuvinte.map((c) => FORMA_TITLU[faraDiacritice(c)] || c);
    FORMA_LANGA.forEach(({ daca, schimba }) => {
      if (!cuvinte.some((c) => daca.includes(faraDiacritice(c)))) return;
      cuvinte = cuvinte.map((c) => schimba[faraDiacritice(c)] || c);
    });

    const campuri = [];
    TIPURI.forEach(({ cuvinte: chei, cere, fara, insotitoare, tip }) => {
      if (campuri.length || !cuvinte.some((c) => chei.includes(faraDiacritice(c)))) return;
      if (cere && !cuvinte.some((c) => cere.includes(faraDiacritice(c)))) return;
      if (fara && cuvinte.some((c) => fara.includes(faraDiacritice(c)))) return;
      campuri.push(`product_type:"${tip}"`);
      cuvinte = cuvinte.filter((c) => !chei.includes(faraDiacritice(c)) && !insotitoare.includes(faraDiacritice(c)));
    });

    const optionale = optionaleIn(cuvinte);
    const neobligatoriu = (c) => esteGeneral(c) || optionale.has(faraDiacritice(c));
    if (!campuri.length && cuvinte.every(neobligatoriu)) return curat;
    return [...campuri, ...cuvinte.map((c) => (neobligatoriu(c) ? c : `"${c}"`))].join(' ');
  }

  /**
   * Cautarea cu un an ("covorase tucson 2023"): textul fara an si anul pentru filtrul An fabricatie.
   * @param {string} text - Textul tastat.
   * @returns {{an: string, rest: string, q: string}|null} Anul, textul fara an si forma lui stricta sau null.
   */
  function anCautat(text) {
    const curat = (text || '').trim();
    if (!curat || /["():*]/.test(curat)) return null;
    const cuvinte = curat.split(/\s+/);
    const ani = cuvinte.filter((c) => ESTE_AN.test(c));
    if (ani.length !== 1 || cuvinte.length < 2 || Number(ani[0]) > new Date().getFullYear() + 1) return null;
    const poz = cuvinte.indexOf(ani[0]);
    if (poz > 0 && MODEL_NUMERIC.has(faraDiacritice(cuvinte[poz - 1]))) return null;
    const rest = cuvinte.filter((c, i) => i !== poz).join(' ');
    if (baterie(rest) || cuvinte.every((c) => c === ani[0] || esteGeneral(c))) return null;
    return { an: ani[0], rest, q: strict(rest) };
  }

  // Casetele de cautare: textul pleaca in forma stricta.
  document.addEventListener('submit', (evt) => {
    if (evt.defaultPrevented) return;
    const form = evt.target;
    if (!(form instanceof HTMLFormElement) || !form.matches('form.search, form[role="search"]')) return;

    let url;
    try {
      url = new URL(form.getAttribute('action') || '', window.location.origin);
    } catch (error) {
      return;
    }
    if (!esteCautare(url)) return;

    const camp = form.querySelector('input[name="q"]');
    if (!camp) return;
    const tastat = camp.value.trim();
    // Dupa butonul Inapoi formularul poate pastra campurile de la cautarea de dinainte.
    form.querySelectorAll('input[data-rg-baterie]').forEach((vechi) => vechi.remove());
    const bat = baterie(tastat);
    if (bat) {
      camp.value = bat.q;
      [['rg_q', tastat], bat.filtru].forEach(([nume, valoare]) => {
        const ascuns = document.createElement('input');
        ascuns.type = 'hidden';
        ascuns.name = nume;
        ascuns.value = valoare;
        ascuns.dataset.rgBaterie = '1';
        form.append(ascuns);
      });
      return;
    }

    const an = anCautat(tastat);
    if (an) {
      camp.value = an.q;
      form.querySelectorAll('input[name="rg_q"]').forEach((vechi) => vechi.remove());
      [['rg_q', tastat], [AN_FAB, an.an]].forEach(([nume, valoare]) => {
        const ascuns = document.createElement('input');
        ascuns.type = 'hidden';
        ascuns.name = nume;
        ascuns.value = valoare;
        ascuns.dataset.rgBaterie = '1'; // acelasi semn: campurile puse de noi, scoase la urmatoarea cautare
        form.append(ascuns);
      });
      return;
    }

    const text = strict(tastat);
    if (text === tastat) return;
    camp.value = text;

    let original = form.querySelector('input[name="rg_q"]');
    if (/\w+:"/.test(text)) {
      if (!original) {
        original = document.createElement('input');
        original.type = 'hidden';
        original.name = 'rg_q';
        form.append(original);
      }
      original.value = tastat;
    } else if (original) {
      original.remove();
    }
  });

  // Legaturile catre cautare (sugestiile de sub caseta): aceeasi forma stricta.
  document.addEventListener('click', (evt) => {
    const link = evt.target instanceof Element ? evt.target.closest('a[href]') : null;
    if (!link) return;

    let url;
    try {
      url = new URL(link.getAttribute('href'), window.location.origin);
    } catch (error) {
      return;
    }
    if (url.origin !== window.location.origin || !esteCautare(url) || url.searchParams.has('rg_lax')) return;

    const text = url.searchParams.get('q');
    if (!text) return;

    const bat = baterie(text);
    if (bat) {
      // Pe pagina de rezultate legaturile (sterge filtrul, paginare) raman cum sunt: filtrul scos nu trebuie sa revina.
      if (link.closest('.cc-main-search')) return;
      url.searchParams.set('q', bat.q);
      url.searchParams.set('rg_q', text.trim());
      url.searchParams.set(...bat.filtru);
      link.setAttribute('href', `${url.pathname}${url.search}${url.hash}`);
      return;
    }

    const an = anCautat(text);
    if (an) {
      if (link.closest('.cc-main-search')) return;
      url.searchParams.set('q', an.q);
      url.searchParams.set('rg_q', text.trim());
      url.searchParams.set(AN_FAB, an.an);
      link.setAttribute('href', `${url.pathname}${url.search}${url.hash}`);
      return;
    }

    const textStrict = strict(text);
    if (textStrict === text.trim()) return;

    url.searchParams.set('q', textStrict);
    if (/\w+:"/.test(textStrict)) url.searchParams.set('rg_q', text.trim());
    link.setAttribute('href', `${url.pathname}${url.search}${url.hash}`);
  }, true);

  /**
   * Un mesaj scurt deasupra rezultatelor.
   * @param {Element} sectiune - Sectiunea paginii de cautare.
   * @param {string} text - Textul mesajului.
   * @param {string} [textLink] - Textul legaturii.
   * @param {string} [adresa] - Adresa legaturii.
   */
  function mesaj(sectiune, text, textLink, adresa) {
    const p = document.createElement('p');
    p.className = 'rg-cautare-mesaj reading-width text-center mx-auto mb-6';
    p.style.fontSize = '0.95em';
    p.append(document.createTextNode(text));

    if (textLink && adresa) {
      const a = document.createElement('a');
      a.href = adresa;
      a.textContent = textLink;
      a.style.textDecoration = 'underline';
      p.append(document.createTextNode(' '), a);
    }

    const capat = sectiune.querySelector('.container > div');
    if (capat) capat.insertAdjacentElement('afterend', p);
    else sectiune.prepend(p);
  }

  /**
   * Adresa unei cautari: forma stricta (cu rg_q cand e nevoie) sau cea obisnuita (rg_lax).
   * @param {URL} url - Adresa paginii.
   * @param {string} tastat - Textul tastat de client.
   * @param {string|null} lax - Valoarea rg_lax (null = cautare stricta).
   * @param {boolean} [faraFiltre] - Scoate filtrele puse.
   * @returns {string} Adresa.
   */
  function adresaCautare(url, tastat, lax, faraFiltre = true) {
    const adresa = new URL(url);
    adresa.searchParams.delete('page');
    adresa.searchParams.delete('rg_q');
    adresa.searchParams.delete('rg_lax');
    adresa.searchParams.delete('rg_an_pas');
    const bat = baterie(tastat);
    const an = !lax && !bat ? anCautat(tastat) : null;
    if (lax) {
      adresa.searchParams.set('q', tastat);
      adresa.searchParams.set('rg_lax', lax);
    } else {
      const text = bat ? bat.q : an ? an.q : strict(tastat);
      adresa.searchParams.set('q', text);
      if (bat || an || /\w+:"/.test(text)) adresa.searchParams.set('rg_q', tastat);
    }
    if (faraFiltre) [...adresa.searchParams.keys()].filter((k) => k.startsWith('filter.')).forEach((k) => adresa.searchParams.delete(k));
    if (!lax && bat) adresa.searchParams.set(...bat.filtru);
    if (an) adresa.searchParams.set(AN_FAB, an.an);
    return `${adresa.pathname}${adresa.search}`;
  }

  /** Pagina de rezultate: textul fara ghilimele si mesajele. */
  function pagina() {
    const sectiune = document.querySelector('.cc-main-search');
    if (!sectiune) return;

    const url = new URL(window.location.href);
    const text = url.searchParams.get('q') || '';
    const lax = url.searchParams.get('rg_lax');
    // Textul tastat: din rg_q sau, dupa un filtru (care nu pastreaza rg_q), din sesiunea browserului.
    let tastat = (url.searchParams.get('rg_q') || '').trim();
    try {
      if (tastat) sessionStorage.setItem(`rg_q:${text}`, tastat);
      else tastat = sessionStorage.getItem(`rg_q:${text}`) || '';
    } catch (error) {
      // Fara sesiune: ramane textul fara ghilimele.
    }
    // Textul cu an ramas in sesiune nu e al unei cautari fara filtrul An ("covorase tucson" dupa "covorase tucson 2023").
    if (!url.searchParams.has('rg_q') && anCautat(tastat) && !url.searchParams.has(AN_FAB) && !url.searchParams.has('rg_an_pas')) {
      tastat = '';
    }
    const curat = tastat || faraGhilimele(text);

    const areFiltre = [...url.searchParams].some(([nume, valoare]) => nume.startsWith('filter.') && valoare !== '');
    const areRezultate = sectiune.querySelector('#filter-results li.js-pagination-result');

    // Cautarea de baterii: amperaj negasit -> toate bateriile de pornire; caseta pastreaza textul tastat.
    if (url.searchParams.has('rg_q') && baterie(curat)) {
      const ah = url.searchParams.get(AMPERAJ);
      if (ah && !areRezultate) {
        const adresa = new URL(url);
        adresa.searchParams.delete(AMPERAJ);
        adresa.searchParams.set(TIP_PRODUS, BATERIE.tip);
        adresa.searchParams.set('rg_lipsa', ah);
        window.location.replace(`${adresa.pathname}${adresa.search}`);
        return;
      }
      document.querySelectorAll('form.search input[name="q"]').forEach((camp) => {
        camp.value = curat;
        camp.setAttribute('value', curat);
      });
      document.title = document.title.replace(/„[^”]*”/, `„${curat}”`);
      const lipsa = url.searchParams.get('rg_lipsa');
      mesaj(sectiune, lipsa
        ? `Nu avem baterie de ${lipsa}. Îți arătăm toate bateriile auto și de tractor; alege amperajul din filtre.`
        : `Afișăm bateriile auto și de tractor pentru „${curat}”.`,
      'Cauți baterii AA, AAA sau CR? Vezi toate rezultatele', adresaCautare(url, curat, '2'));
      return;
    }

    // Cautarea cu an: filtrul An fabricatie -> negasit: numarul ca text (model, ex. John Deere 2030) -> fara numar -> obisnuita.
    const cuAn = anCautat(curat);
    const pas = url.searchParams.get('rg_an_pas');
    if (cuAn && (url.searchParams.has(AN_FAB) || pas)) {
      if (!areRezultate) {
        if (pas === '3') {
          window.location.replace(adresaCautare(url, cuAn.rest, '1', false));
          return;
        }
        const adresa = new URL(url);
        adresa.searchParams.delete(AN_FAB);
        adresa.searchParams.delete('page');
        adresa.searchParams.set('q', pas ? cuAn.q : `${cuAn.q} "${cuAn.an}"`);
        adresa.searchParams.set('rg_an_pas', pas ? '3' : '2');
        window.location.replace(`${adresa.pathname}${adresa.search}`);
        return;
      }
      document.querySelectorAll('form.search input[name="q"]').forEach((camp) => {
        camp.value = curat;
        camp.setAttribute('value', curat);
      });
      document.title = document.title.replace(/„[^”]*”/, `„${curat}”`);
      if (pas === '3') {
        mesaj(sectiune, `Nu avem produse marcate pentru anul ${cuAn.an}. Îți arătăm toate rezultatele pentru „${cuAn.rest}”; verifică anul în titlu.`);
      } else if (pas === '2') {
        mesaj(sectiune, `Afișăm produsele potrivite pentru „${curat}”.`);
      } else {
        mesaj(sectiune, `Afișăm produsele pentru „${cuAn.rest}” potrivite anului ${cuAn.an}.`,
          `Vezi toate rezultatele pentru „${cuAn.rest}”`, adresaCautare(url, cuAn.rest, null));
      }
      return;
    }

    // Cautarea stricta nu a gasit nimic (fara filtre puse): trecem la cautarea obisnuita.
    if (esteStrict(text) && !areRezultate && !areFiltre) {
      window.location.replace(adresaCautare(url, curat, '1', false));
      return;
    }

    if (esteStrict(text)) {
      // Doar casetele vizibile: campul ascuns al filtrelor trebuie sa ramana cu ghilimele.
      document.querySelectorAll('form.search input[name="q"]').forEach((camp) => {
        if (esteStrict(camp.value)) {
          camp.value = curat;
          camp.setAttribute('value', camp.value);
        }
      });
      document.title = document.title.replace(/„[^”]*”/, `„${curat}”`);

      if (areRezultate) {
        mesaj(sectiune, `Afișăm produsele potrivite pentru „${curat}”.`,
          'Vezi și rezultate asemănătoare', adresaCautare(url, curat, '2'));
      }
    } else if (lax === '1' && curat) {
      mesaj(sectiune, `Nu am găsit produse care să conțină toate cuvintele „${curat}”. Îți arătăm rezultate asemănătoare.`);
    } else if (lax === '2' && curat) {
      mesaj(sectiune, `Rezultate asemănătoare pentru „${curat}” (pot include și alte produse).`,
        'Înapoi la potrivirile exacte', adresaCautare(url, curat, null));
    }
  }

  // La revenirea cu butonul Inapoi, caseta poate pastra ghilimelele.
  window.addEventListener('pageshow', () => {
    const tastat = (new URL(window.location.href).searchParams.get('rg_q') || '').trim();
    document.querySelectorAll('form.search input[name="q"]').forEach((camp) => {
      if (esteStrict(camp.value)) camp.value = tastat || faraGhilimele(camp.value);
    });
  });

  window.rgCautareStricta = { strict, esteStrict, faraGhilimele, anCautat };

  pagina();
})();
